import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Check, ChevronRight as ChevronRightIcon, X } from 'lucide-react';
import { sendVerificationCode, verifyCode } from '../api/verification';
import { createUser } from '../api/users';
import { getTerms } from '../api/terms';
import TermsModal from '../components/TermsModal';
import styles from './SignupPage.module.css';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import Logo from '../components/Logo';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TIMER_SECONDS = 300;

function formatTime(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function validatePassword(pw) {
  if (pw.length < 8 || pw.length > 20) return '8~20자로 입력해주세요';
  if (/\s/.test(pw)) return '공백은 사용할 수 없어요';
  if (!/[A-Za-z]/.test(pw)) return '영문을 포함해야 해요';
  if (!/[0-9]/.test(pw)) return '숫자를 포함해야 해요';
  if (!/[^A-Za-z0-9\s]/.test(pw)) return '특수문자를 포함해야 해요';
  return null;
}

function EyeIcon({ open }) {
  const Comp = open ? Eye : EyeOff;
  return <Comp size={20} strokeWidth={1.8} />;
}

function CheckIcon() {
  return <Check size={12} strokeWidth={3} />;
}

function ChevronRight() {
  return <ChevronRightIcon size={16} strokeWidth={2} />;
}

function AgreeLine({ checked, onToggle, onView, label, required }) {
  return (
    <div className={styles.agreeLine}>
      <button type="button" className={`${styles.agreeCircle} ${checked ? styles.agreeCircleChecked : ''}`} onClick={onToggle}>
        {checked && <CheckIcon />}
      </button>
      <button type="button" className={styles.agreeLabelWrap} onClick={onView}>
        {required && <span className={styles.agreeRequired}>필수</span>}
        <span className={styles.agreeLabel}>{label}</span>
      </button>
      <button type="button" className={styles.agreeChevron} onClick={onView}>
        <ChevronRight />
      </button>
    </div>
  );
}

function AgreeAllLine({ checked, onToggle }) {
  return (
    <button type="button" className={styles.agreeAll} onClick={onToggle} aria-pressed={checked}>
      <span className={`${styles.agreeCircle} ${checked ? styles.agreeCircleChecked : ''}`}>
        {checked && <CheckIcon />}
      </span>
      <span className={styles.agreeAllLabel}>약관에 모두 동의합니다</span>
    </button>
  );
}

function EmailExistsModal({ onClose, onLogin, onFindAccount }) {
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>이미 가입된 이메일이에요</h3>
          <button type="button" className={styles.modalClose} onClick={onClose}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>
        <p className={styles.modalSub}>로그인 또는 계정찾기를 진행해주세요</p>
        <div className={styles.modalBtns}>
          <button type="button" className={styles.modalPrimary} onClick={onLogin}>로그인하기</button>
          <button type="button" className={styles.modalSecondary} onClick={onFindAccount}>계정찾기</button>
        </div>
      </div>
    </div>
  );
}

export default function SignupPage() {
  useDocumentMeta({ title: '회원가입', noIndex: true });
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');
  const [timer, setTimer] = useState(0);
  const [codeError, setCodeError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [termsList, setTermsList] = useState([]);
  const [agreedMap, setAgreedMap] = useState({});

  const [termsModal, setTermsModal] = useState(null); // { type: 'terms'|'privacy', termId }

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const timerRef = useRef(null);
  useEffect(() => () => clearInterval(timerRef.current), []);

  useEffect(() => {
    getTerms().then((list) => {
      const arr = Array.isArray(list) ? list : [];
      setTermsList(arr);
      const init = {};
      arr.forEach((t) => { init[t.termId] = false; });
      setAgreedMap(init);
    }).catch(() => {});
  }, []);

  const emailValid = EMAIL_RE.test(email.trim());
  const emailError = emailTouched && email.length > 0 && !emailValid;
  const pwError = passwordTouched && password.length > 0 ? validatePassword(password) : null;
  const confirmError = confirmTouched && passwordConfirm.length > 0 && passwordConfirm !== password
    ? '비밀번호가 일치하지 않아요' : null;
  const passwordsMatch = password.length > 0 && password === passwordConfirm;
  const allRequiredAgreed = termsList.filter((t) => t.required).every((t) => agreedMap[t.termId]);

  const canSubmit = emailValid && codeVerified && !validatePassword(password) && passwordsMatch && allRequiredAgreed;

  const startTimer = () => {
    clearInterval(timerRef.current);
    setTimer(TIMER_SECONDS);
    timerRef.current = setInterval(() => {
      setTimer((prev) => { if (prev <= 1) { clearInterval(timerRef.current); return 0; } return prev - 1; });
    }, 1000);
  };

  const handleSendCode = async () => {
    if (!emailValid || sendingCode || codeVerified) return;
    setSendingCode(true);
    setCodeError('');
    setCode('');
    setCodeVerified(false);
    try {
      await sendVerificationCode(email.trim());
      setCodeSent(true);
      startTimer();
    } catch (e) {
      setCodeError(e?.response?.data?.message ?? '인증 메일 전송에 실패했어요');
    } finally {
      setSendingCode(false);
    }
  };

  const doVerify = async (digits) => {
    if (verifyingCode || codeVerified) return;
    setVerifyingCode(true);
    setCodeError('');
    try {
      const result = await verifyCode(email.trim(), digits);
      setVerificationToken(result?.verificationToken ?? '');
      setCodeVerified(true);
      clearInterval(timerRef.current);
    } catch (e) {
      setCodeError(e?.response?.data?.message ?? '인증번호가 올바르지 않습니다');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleCodeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setCodeError('');
    if (digits.length === 6 && !codeVerified) doVerify(digits);
  };

  const handleToggleTerm = (termId) => {
    setAgreedMap((prev) => ({ ...prev, [termId]: !prev[termId] }));
  };

  const allAgreed = termsList.length > 0 && termsList.every((t) => agreedMap[t.termId]);

  // 이미 다 동의한 상태면 전체 해제로 동작한다
  const handleToggleAll = () => {
    const next = !allAgreed;
    setAgreedMap(Object.fromEntries(termsList.map((t) => [t.termId, next])));
  };

  const handleViewTerm = (term) => {
    if (term.type === 'TERMS') setTermsModal({ type: 'terms', termId: term.termId });
    else if (term.type === 'PRIVACY') setTermsModal({ type: 'privacy', termId: term.termId });
  };

  // 전문을 읽고 바로 동의 — 닫은 뒤 체크박스를 다시 찾지 않아도 된다
  const handleAgreeFromModal = () => {
    setAgreedMap((prev) => ({ ...prev, [termsModal.termId]: true }));
    setTermsModal(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const agreements = termsList.map((t) => ({ termId: t.termId, agreed: agreedMap[t.termId] ?? false }));
      await createUser({
        email: email.trim(),
        password,
        verificationToken,
        phoneNumber: phone || undefined,
        agreements,
      });
      navigate('/login');
    } catch (e) {
      const msg = e?.response?.data?.data?.message ?? e?.response?.data?.message ?? '';
      if (msg.includes('이미') && msg.includes('이메일')) {
        setShowDuplicateModal(true);
      } else {
        setSubmitError(msg || '회원가입에 실패했어요. 다시 시도해주세요.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.page}>        <div className={styles.body}>
          <Link className={styles.logo} to="/"><Logo height={26} /></Link>
          <h1 className={styles.heading}>회원가입</h1>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>

            {/* 이메일 */}
            <div className={styles.field}>
              <label htmlFor="signup-email" className={styles.label}>이메일</label>
              <input
                id="signup-email"
                type="email"
                className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                placeholder="이메일을 입력해주세요"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (codeSent) {
                    setCodeSent(false); setCodeVerified(false);
                    setCode(''); setCodeError('');
                    clearInterval(timerRef.current); setTimer(0);
                  }
                }}
                onBlur={() => setEmailTouched(true)}
                autoComplete="email"
              />
              {emailError && <p className={styles.fieldError}>이메일 형식을 확인해주세요</p>}
            </div>

            {/* 인증번호 */}
            <div className={styles.field}>
              <label htmlFor="signup-code" className={styles.label}>인증번호</label>
              <div className={styles.codeRow}>
                <input id="signup-code"
                  type="text"
                  inputMode="numeric"
                  className={`${styles.input} ${styles.codeInput} ${codeError ? styles.inputError : ''}`}
                  placeholder="인증번호 6자리"
                  value={code}
                  onChange={handleCodeChange}
                  disabled={!codeSent || codeVerified}
                  maxLength={6}
                />
                {codeSent && !codeVerified && timer > 0 && (
                  <span className={`${styles.timer} ${timer < 60 ? styles.timerRed : ''}`}>{formatTime(timer)}</span>
                )}
                <button
                  type="button"
                  className={`${styles.codeBtn} ${!emailValid || sendingCode || codeVerified ? styles.codeBtnDisabled : ''}`}
                  onClick={handleSendCode}
                  disabled={!emailValid || sendingCode || codeVerified}
                >
                  {sendingCode ? '전송 중…' : codeSent ? '재전송' : '전송'}
                </button>
              </div>
              {verifyingCode && <p className={styles.hintMsg}>인증 확인 중…</p>}
              {codeVerified && (
                <p className={styles.verifiedMsg}><Check size={13} strokeWidth={3} /> 인증이 완료되었습니다</p>
              )}
              {codeError && <p className={styles.fieldError}>{codeError}</p>}
            </div>

            {/* 인증 후에만 노출 */}
            {codeVerified && (
              <>
                {/* 휴대폰 번호 (선택) */}
                <div className={styles.field}>
                  <div className={styles.labelRow}>
                    <label htmlFor="signup-phone" className={styles.label}>휴대폰 번호</label>
                    <span className={styles.labelOpt}>(선택)</span>
                  </div>
                  <input id="signup-phone"
                    type="tel"
                    inputMode="numeric"
                    className={styles.input}
                    placeholder="숫자만 입력해주세요"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    autoComplete="tel"
                  />
                </div>

                {/* 비밀번호 */}
                <div className={styles.field}>
                  <label htmlFor="signup-password" className={styles.label}>비밀번호</label>
                  <p className={styles.hint}>8~20자, 영문·숫자·특수문자 포함, 공백 불가</p>
                  <div className={styles.pwWrap}>
                    <input id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      className={`${styles.input} ${styles.pwInput} ${pwError ? styles.inputError : ''}`}
                      placeholder="비밀번호를 입력해주세요"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setPasswordTouched(true)}
                      autoComplete="new-password"
                    />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowPassword((p) => !p)}>
                      <EyeIcon open={showPassword} />
                    </button>
                  </div>
                  {pwError && <p className={styles.fieldError}>{pwError}</p>}
                </div>

                {/* 비밀번호 확인 */}
                <div className={styles.field}>
                  <label htmlFor="signup-password-confirm" className={styles.label}>비밀번호 확인</label>
                  <div className={styles.pwWrap}>
                    <input id="signup-password-confirm"
                      type={showConfirm ? 'text' : 'password'}
                      className={`${styles.input} ${styles.pwInput} ${confirmError ? styles.inputError : ''}`}
                      placeholder="비밀번호를 다시 입력해주세요"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      onBlur={() => setConfirmTouched(true)}
                      autoComplete="new-password"
                    />
                    <button type="button" className={styles.eyeBtn} onClick={() => setShowConfirm((p) => !p)}>
                      <EyeIcon open={showConfirm} />
                    </button>
                  </div>
                  {confirmError && <p className={styles.fieldError}>{confirmError}</p>}
                  {passwordsMatch && !confirmError && <p className={styles.verifiedMsg}><Check size={13} strokeWidth={3} /> 비밀번호가 일치합니다</p>}
                </div>

                {/* 약관 동의 */}
                {termsList.length > 0 && (
                  <div className={styles.agreeSection}>
                    <p className={styles.agreeSectionTitle}>약관 동의</p>
                    <AgreeAllLine checked={allAgreed} onToggle={handleToggleAll} />
                    <div className={styles.agreeList}>
                      {termsList.map((term, i) => (
                        <div key={term.termId}>
                          {i > 0 && <div className={styles.agreeDivider} />}
                          <AgreeLine
                            checked={agreedMap[term.termId] ?? false}
                            onToggle={() => handleToggleTerm(term.termId)}
                            onView={() => handleViewTerm(term)}
                            label={term.name}
                            required={term.required}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {submitError && <p className={styles.submitError} role="alert">{submitError}</p>}

            <button
              type="submit"
              className={`${styles.submitBtn} ${!canSubmit ? styles.submitBtnDisabled : ''}`}
              disabled={!canSubmit || submitting}
            >
              {submitting ? '처리 중…' : '가입하기'}
            </button>

            <p className={styles.loginRow}>
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className={styles.loginLink}>로그인</Link>
            </p>

            <Link to="/" className={styles.exitLink}>가입 없이 둘러보기</Link>

          </form>
        </div>
      </div>

      {termsModal && (
        <TermsModal
          type={termsModal.type}
          onClose={() => setTermsModal(null)}
          onAgree={handleAgreeFromModal}
        />
      )}

      {showDuplicateModal && (
        <EmailExistsModal
          onClose={() => setShowDuplicateModal(false)}
          onLogin={() => { setShowDuplicateModal(false); navigate('/login'); }}
          onFindAccount={() => { setShowDuplicateModal(false); navigate('/reset-password'); }}
        />
      )}
    </>
  );
}
